import { ORDER_ITEM_STATUS_INTERNAL } from './Order';
import { ProductCardData } from './Product';
import { InsuranceProduct, SubscriptionProduct } from './Subscription';

/** These types borrowed from Closet Service */
export enum CLOSET_SLOT_TIER_SLUG {
  PREMIER = 'premier',
  CLASSIQUE = 'classique',
  COUTURE = 'couture',
  PRIVEE = 'privee'
}

export enum CLOSET_SLOT_TIER_STRING {
  PREMIER = 'Premier',
  CLASSIQUE = 'Classique',
  COUTURE = 'Couture'
}

export type ClosetItem = {
  id: string;
  name: string;
  slot: string;
  tier: CLOSET_SLOT_TIER_SLUG;
  imageUrl: string;
  status: ORDER_ITEM_STATUS_INTERNAL;
  orderNumber: string;
  orderDate: Date;
  swapProgress: number;
  orderId: string;
  productId: string;
  productInventoryId: string;
  childProductId: string;
  childProductSku?: string;
  canEarlySwap: boolean;
  origin?: string;
  productSku?: string;
  shipmentLocation?: string;
  shipmentTrackingNumber?: string | null;
  shipmentTrackingStatus?: string | null;
  returnTrackingNumber?: string | null;
  returnTrackingStatus?: string | null;
  /** ISO8601 Date string */
  eligibleForSwapDate?: string | null;
  orderShortId?: string;
  productCardData: ProductCardData;
};

export type ClosetSlot = {
  maxTier: CLOSET_SLOT_TIER_SLUG;
  isOpen: boolean;
  weight: number;
  currentItem: ClosetItem | null;
  returnedItem: ClosetItem | null;
};

export type Closet = {
  /** Plan IDs as returned by closet payload */
  subscriptionPlans: string[];
  insurancePlans: string[];

  /** Plan products as dynamically computed by GET api/closet route */
  subscriptionPlanProducts: SubscriptionProduct[];
  priveeSubscriptionProduct?: SubscriptionProduct;
  insurancePlanProducts: InsuranceProduct[];

  downgradePlanOptions?: string[][];
  isOpen: boolean;
  isEmpty: boolean;
  slots: ClosetSlot[];
  otherOrderItems: ClosetItem[];
  privee?: {
    isOpen: boolean;
    currentItem: ClosetItem | null;
    returnedItem: ClosetItem | null;
  };
  error?: {
    status: number;
  };
};

export type ValidateAgainstClosetRequest = {
  tiers: CLOSET_SLOT_TIER_SLUG[];
};

export type ClosetError = {
  action_needed?: string;
  error_swap_date?: string;

  /**
   * List of error codes available either:
   * - In Postman collection documentation: https://github.com/Vivrelle/postman-collection
   * - In Closet codebase: https://github.com/Vivrelle/closet-microservice/blob/1.1.5/utils/errors.ts#L18
   */
  error_code?: string;
};

/**
 * Returns a string from the closet service regarding the user's eligibility
 * to add a product to their cart with their current closet.
 * * - In Closet codebase: https://github.com/Vivrelle/closet-microservice/blob/1.1.5/utils/errors.ts#L18
 */
export enum ClosetValidationStatus {
  Upgrade = 'ERR_UPGRADE',
  TooEarly = 'ERR_TOO_EARLY',
  TooEarlyOrUpgrade = 'ERR_TOO_EARLY_OR_UPGRADE',
  UpgradeOrReturn = 'ERR_UPGRADE_OR_RETURN',
  Return = 'ERR_RETURN',
  Resubscribe = 'ERR_RESUBSCRIBE',
  NotApproved = 'ERR_NOT_APPROVED',
  ReapprovalNeeded = 'ERR_RE_APPROVAL',
  PaymentNeeded = 'ERR_PAYMENT',
  Generic = 'ERR_GENERIC',

  /** Computed status: this does not come from the closet API */
  EmptyCart = 'ERR_EMPTY_CART',

  /** Computed status: this does not come from the closet API */
  ItemInCart = 'ERR_ITEM_IN_CART'
}

export type CartTierValidationResponse = {
  cart_id: string | null;
  cart_tiers: string[];
  tiers: string[];
  is_valid: boolean;
  is_valid_with_cart_emptied: boolean;
  error?: ClosetError;
};

export type ClosetSubscriptionOptions = {
  canDowngrade: boolean;
  canUpgrade: boolean;
  canFreeze: boolean;
  canCancel: boolean;
  hasMetMinimum: boolean;
  cancellationMinimumWaiver?: {
    source: string | null;
    monthsRequired: number;
    canCancelWithWaiver: boolean;
  } | null;
  canSubscribePrivee: boolean;
  canFreezePrivee: boolean;
  canUnfreezePrivee: boolean;
  canCancelPrivee: boolean;
  priveePlanId: string | null;
  priveeFrozenPlanId: string | null;
  currentPlans: {
    subscriptionPlans: string[];
    insurancePlans: string[];
  };
  downgradePlanOptions: string[][];
  upgradePlanOptions: string[][];
  insuranceOptions: string[][];
};
